import { useMemo, useState } from "react"

import { scaleLinear, scaleUtc } from "d3-scale"
import { line } from "d3-shape"
import type { LayoutChangeEvent } from "react-native"
import { View } from "react-native"
import Svg, { Circle, G, Line, Path, Text as SvgText } from "react-native-svg"

import { Box, HStack, Pressable, Text, VStack } from "wealthmode-frontend/gluestack/ui"

import {
  type TimeseriesAxis,
  type TimeseriesPoint,
  type TimeseriesSeries,
  axisDomain,
  axisSlots,
  isShown,
  isolatedPoints,
  monthStarts,
  nearestIndex,
  resolveDomain,
  timeDomain,
  timesOf,
  toggleSelected,
} from "./layout"

export type { TimeseriesAxis, TimeseriesPoint, TimeseriesSeries, TimeseriesSide } from "./layout"

// Grafana's classic palette, in order.
const PALETTE = ["#7EB26D", "#EAB839", "#6ED0E0", "#EF843C", "#E24D42", "#1F78C1", "#BA43A9", "#705DA0"]

const AXIS_COLOR = "#8E8E8E"
const AXIS_LINE_COLOR = "rgba(142, 142, 142, 0.2)"
const GRID_COLOR = "rgba(142, 142, 142, 0.06)"

const DEFAULT_AXES: TimeseriesAxis[] = [{ id: "left", side: "left" }]

// axis is the width each y axis takes beside the plot: room for its widest labels and little more.
const MARGIN = { top: 8, bottom: 24, axis: 44, edge: 12, label: 6 }
const Y_TICKS = 5
const FONT_SIZE = 11
const STROKE = 1.5
const POINT_RADIUS = 3
const TOOLTIP_GAP = 12

/**
 * Timeseries plots series against time the way an operations dashboard does: shared
 * gridlines, a crosshair that reads every series at the hovered time, and a legend that
 * narrows the plot to the series clicked in it. Series on different scales are plotted against axes of their
 * own, on either side; axes on one side stack outward, and an axis carrying one series takes
 * its colour. Gaps in a series break its line, and a value alone between gaps is a point.
 */
export function Timeseries({
  series,
  axes = DEFAULT_AXES,
  height = 320,
  formatValue = String,
  formatTime = (t) => new Date(t).toISOString().slice(0, 10),
  minSpan = 0,
}: {
  series: TimeseriesSeries[]
  /** The y axes, the first of which carries the gridlines and any series naming no axis. */
  axes?: TimeseriesAxis[]
  height?: number
  /** Formats values on an axis with no format of its own. */
  formatValue?: (value: number) => string
  formatTime?: (time: number) => string
  /**
   * The least time, in milliseconds, the x axis covers. A shorter history keeps to the right
   * of it rather than stretching across the plot.
   */
  minSpan?: number
}) {
  const [width, setWidth] = useState(0)
  const [selected, setSelected] = useState<string[]>([])
  const [hover, setHover] = useState<number | null>(null)

  const colored = useMemo(
    () => series.map((s, i) => ({ ...s, axis: s.axis ?? axes[0].id, color: s.color ?? PALETTE[i % PALETTE.length] })),
    [series, axes],
  )
  const shown = colored.filter((s) => isShown(selected, s.name))
  const times = useMemo(() => timesOf(colored), [colored])

  const used = axes.filter((a) => colored.some((s) => s.axis === a.id))
  const slots = axisSlots(used)
  const leftCount = used.filter((a) => a.side === "left").length
  const rightCount = used.filter((a) => a.side === "right").length

  const plotLeft = Math.max(leftCount * MARGIN.axis, MARGIN.edge)
  const plotRight = Math.max(plotLeft, width - Math.max(rightCount * MARGIN.axis, MARGIN.edge))
  const plotTop = MARGIN.top
  const plotBottom = height - MARGIN.bottom

  const xDomain = timeDomain(times, minSpan)
  const x = scaleUtc()
    .domain(xDomain ?? [0, 1])
    .range([plotLeft, plotRight])

  const scales: Record<string, ReturnType<typeof yScale>> = {}
  for (const a of used) {
    scales[a.id] = yScale(resolveDomain(a, axisDomain(colored, a.id)), plotBottom, plotTop)
  }

  const formatters: Record<string, (value: number) => string> = {}
  for (const a of axes) {
    formatters[a.id] = a.format ?? formatValue
  }

  const grid = scales[axes[0].id]

  // Vertical gridlines fall on month boundaries; a span inside one month marks its first day.
  const xTicks = xDomain === null ? [] : monthStarts(xDomain[0], xDomain[1])
  if (times.length > 0 && xTicks.length === 0) {
    xTicks.push(times[0])
  }
  const hoverTime = hover === null ? null : times[hover]

  const onLayout = (e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width)

  const onPointerMove = (offsetX: number) => {
    setHover(nearestIndex(times, x.invert(plotLeft + offsetX).getTime()))
  }

  return (
    <VStack className="gap-3">
      <View
        style={{ height }}
        onLayout={onLayout}
      >
        {width > 0 && (
          <Svg
            width={width}
            height={height}
          >
            {grid &&
              grid.ticks(Y_TICKS).map((tick) => (
                <Line
                  key={`g${tick}`}
                  x1={plotLeft}
                  x2={plotRight}
                  y1={grid(tick)}
                  y2={grid(tick)}
                  stroke={GRID_COLOR}
                />
              ))}

            {used.map((a) => {
              const y = scales[a.id]
              if (!y) return null

              const onAxis = colored.filter((s) => s.axis === a.id)
              const fill = a.color ?? (onAxis.length === 1 ? onAxis[0].color : AXIS_COLOR)
              const left = a.side === "left"
              const edge = left ? plotLeft - slots[a.id] * MARGIN.axis : plotRight + slots[a.id] * MARGIN.axis

              return (
                <G key={`a${a.id}`}>
                  {y.ticks(Y_TICKS).map((tick) => (
                    <SvgText
                      key={tick}
                      x={left ? edge - MARGIN.label : edge + MARGIN.label}
                      y={y(tick) + FONT_SIZE / 3}
                      fontSize={FONT_SIZE}
                      fill={fill}
                      textAnchor={left ? "end" : "start"}
                    >
                      {formatters[a.id](tick)}
                    </SvgText>
                  ))}
                </G>
              )
            })}

            <Line
              x1={plotLeft}
              x2={plotLeft}
              y1={plotTop}
              y2={plotBottom}
              stroke={AXIS_LINE_COLOR}
            />
            <Line
              x1={plotLeft}
              x2={plotRight}
              y1={plotBottom}
              y2={plotBottom}
              stroke={AXIS_LINE_COLOR}
            />

            {xTicks.map((tick) => (
              <G key={`x${tick}`}>
                <Line
                  x1={x(tick)}
                  x2={x(tick)}
                  y1={plotTop}
                  y2={plotBottom}
                  stroke={GRID_COLOR}
                />
                <SvgText
                  x={x(tick)}
                  y={plotBottom + FONT_SIZE + 6}
                  fontSize={FONT_SIZE}
                  fill={AXIS_COLOR}
                  textAnchor="middle"
                >
                  {formatTime(tick)}
                </SvgText>
              </G>
            ))}

            {shown.map((s) => {
              const y = scales[s.axis]
              if (!y) return null

              const path = line<TimeseriesPoint>()
                .defined((p) => p.value !== null)
                .x((p) => x(p.time))
                .y((p) => y(p.value as number))(s.points)

              return (
                <G key={s.name}>
                  <Path
                    d={path ?? ""}
                    stroke={s.color}
                    strokeWidth={STROKE}
                    fill="none"
                  />
                  {isolatedPoints(s.points).map((p) => (
                    <Circle
                      key={p.time}
                      cx={x(p.time)}
                      cy={y(p.value as number)}
                      r={POINT_RADIUS}
                      fill={s.color}
                    />
                  ))}
                </G>
              )
            })}

            {hoverTime !== null && (
              <Line
                x1={x(hoverTime)}
                x2={x(hoverTime)}
                y1={plotTop}
                y2={plotBottom}
                stroke={AXIS_COLOR}
                strokeDasharray="4,4"
              />
            )}
          </Svg>
        )}

        <View
          style={{
            position: "absolute",
            left: plotLeft,
            top: plotTop,
            width: plotRight - plotLeft,
            height: plotBottom - plotTop,
          }}
          onPointerMove={(e) => onPointerMove(e.nativeEvent.offsetX)}
          onPointerLeave={() => setHover(null)}
        />

        {hoverTime !== null && (
          <Box
            pointerEvents="none"
            className="absolute rounded-md border border-border-medium bg-background-secondary px-3 py-2 shadow-lg"
            style={tooltipPosition(x(hoverTime), plotLeft, plotRight, plotTop, width)}
          >
            <Text className="text-xs text-foreground-tertiary mb-1">{formatTime(hoverTime)}</Text>
            {shown.map((s) => {
              const value = s.points.find((p) => p.time === hoverTime)?.value ?? null

              return (
                <HStack
                  key={s.name}
                  className="gap-2 items-center"
                >
                  <Box
                    className="w-3 h-0.5"
                    style={{ backgroundColor: s.color }}
                  />
                  <Text className="text-xs text-foreground-secondary flex-1">{s.name}</Text>
                  <Text className="text-xs text-foreground-primary font-semibold">
                    {value === null ? "—" : formatters[s.axis](value)}
                  </Text>
                </HStack>
              )
            })}
          </Box>
        )}
      </View>

      <HStack className="flex-wrap gap-x-4 gap-y-1 px-2">
        {colored.map((s) => (
          <Pressable
            key={s.name}
            onPress={() => setSelected(toggleSelected(selected, s.name))}
          >
            <HStack
              className="gap-2 items-center"
              style={{ opacity: isShown(selected, s.name) ? 1 : 0.4 }}
            >
              <Box
                className="w-4 h-0.5"
                style={{ backgroundColor: s.color }}
              />
              <Text className="text-sm text-foreground-secondary">{s.name}</Text>
            </HStack>
          </Pressable>
        ))}
      </HStack>
    </VStack>
  )
}

function yScale(domain: [number, number] | null, bottom: number, top: number) {
  if (domain === null) {
    return null
  }

  return scaleLinear().domain(domain).nice(Y_TICKS).range([bottom, top])
}

// The tooltip sits beside the crosshair, flipping to its left past the plot's middle so it
// stays inside the plot.
function tooltipPosition(cursorX: number, plotLeft: number, plotRight: number, top: number, width: number) {
  if (cursorX > (plotLeft + plotRight) / 2) {
    return { top, right: width - cursorX + TOOLTIP_GAP }
  }

  return { top, left: cursorX + TOOLTIP_GAP }
}
